import { Component, OnInit, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastService } from '@app/components/toastr/toast.service';

@Component({
  selector: 'app-amenities-create',
  templateUrl: './amenities-create.component.html',
  styleUrls: ['./amenities-create.component.scss']
})
export class AmenitiesCreateComponent implements OnInit {

  public Form: FormGroup;
  submitted = false;
  message = null;
  class = null;
  action = null;
  loading = false;

  constructor(
    private ActivatedRoute: ActivatedRoute, 
    private formBuilder: FormBuilder,  
    private http: HttpClient, 
    private router: Router, 
    @Inject('API_ENDPOINT') private api_endpoint: string,
    private toast: ToastService
  ) {}

  ngOnInit() {
    this.action = 'Submit';
    
    // Initialize the form
    this.Form = this.formBuilder.group({
      amenity: ['', Validators.required],
      _id: [''],
    });

    // Check if we're editing an existing amenity
    this.ActivatedRoute.params.subscribe(params => {
      let id = params['id'];
      if (id) {
        this.loadAmenity(id);
      } else {
        // For testing: If no ID in route, try to autofill with your test data
        this.autoFillTestData();
      }
    });
  }

  // Getter for easy access to form fields
  get f() { return this.Form.controls; }

  // Load amenity data for editing
  loadAmenity(id: string) {
    this.loading = true;
    const headers = this.getAuthHeaders();
    
    this.http.get<any>(`${environment.apiUrl}/amenity/${id}`, { headers })
      .subscribe(
        (data: any) => {
          this.loading = false;
          
          if (data.message === 'Logged Out!') {
            this.router.navigateByUrl('/auth/login');
            return;
          }
          
          // Check different possible response structures
          if (data.data) {
            // If response has data property
            this.action = 'Update';
            this.Form.patchValue({
              _id: data.data._id || data.data.id,
              amenity: data.data.amenity,
            });
          } else if (data.amenity) {
            // If response is the amenity object directly
            this.action = 'Update';
            this.Form.patchValue({
              _id: data._id || data.id,
              amenity: data.amenity,
            });
          } else {
            console.error('Unexpected API response structure:', data);
            this.showMessage('Failed to load amenity data. Unexpected response format.', 'danger');
          }
        },
        (err: HttpErrorResponse) => {
          this.loading = false;
          this.handleError(err, 'Loading amenity failed');
        }
      );
  }

  // Method to autofill with test data (for demonstration)
  autoFillTestData() {
    const testData = {"amenity":"","id":""};
    
    // Check if we're in development mode or have a specific flag for testing
    if (!environment.production || localStorage.getItem('enableTestData') === 'true') {
      this.action = 'Update';
      this.Form.patchValue({
        _id: testData.id,
        amenity: testData.amenity,
      });
      this.showMessage('Data loaded successfully', 'success');
    }
  }

  // Handle form submission
  onFormSubmit() {
    this.submitted = true;

    if (this.Form.invalid) {
      return;
    }

    this.loading = true;
    const headers = this.getAuthHeaders();

    if (this.action === 'Submit') {
      this.createAmenity(headers);
    } else {
      this.updateAmenity(headers);
    }
  }

  // Create a new amenity
  createAmenity(headers: HttpHeaders) {
    const data = {
      "amenity": this.Form.value.amenity,
    };

    this.http.post<any>(`${environment.apiUrl}/Amenity/`, data, { headers })
      .subscribe(
        res => {
          this.loading = false;
          if (res.message === 'Logged Out!') {
            this.router.navigateByUrl('/auth/login');
            return;
          }
          
          if (res.status === '500') {
            this.showMessage(res.message, 'danger');
          } else {
            this.showMessage('Successfully Added', 'success');
             this.toast.show("Successfully Added", "success");
            setTimeout(() => {
              this.router.navigateByUrl('/amenities/list');
            }, 1500);
          }
        },
        (err: HttpErrorResponse) => {
          this.loading = false;
          this.handleError(err, 'Creating amenity failed');
        }
      );
  }

  // Update an existing amenity
  updateAmenity(headers: HttpHeaders) {
    const data = {
      "amenity": this.Form.value.amenity,
      "_id": this.Form.value._id,
    };

    this.http.put<any>(`${environment.apiUrl}/Amenity/`, data, { headers })
      .subscribe(
        res => {
          this.loading = false;
          if (res.message === 'Logged Out!') {
            this.router.navigateByUrl('/auth/login');
            return;
          }
          
          if (res.status === '500') {
            this.showMessage(res.message, 'danger');
          } else {
            this.showMessage('Successfully Updated', 'success');
             this.toast.show("Successfully Updated", "success");
            setTimeout(() => {
              this.router.navigateByUrl('/amenities/list');
            }, 1500);
          }
        },
        (err: HttpErrorResponse) => {
          this.loading = false;
          this.handleError(err, 'Updating amenity failed');
        }
      );
  }

  // Get authentication headers with Bearer token
  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('authToken');
    
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    });

    // Add Userinfo if still required by your API
    const userInfo = JSON.stringify({
      _id: localStorage.getItem('adminSess'), 
      role: localStorage.getItem('userRole')
    });
    
    if (userInfo) {
      headers = headers.set('Userinfo', userInfo);
    }

    return headers;
  }

  // Show message to user
  private showMessage(message: string, type: string) {
    this.message = message;
    this.class = type === 'success' ? 'alert alert-success' : 'alert alert-danger';
    // Scroll to top to show message
    window.scrollTo(0, 0);
    
    // Auto hide message after 5 seconds
    setTimeout(() => {
      this.message = null;
    }, 5000);
  }

  // Handle HTTP errors
  private handleError(error: HttpErrorResponse, context: string) {
    console.error(`${context}:`, error);
    
    if (error.error instanceof Error) {
      // Client-side error
      this.showMessage('An error occurred. Please check your connection and try again.', 'danger');
    } else {
      // Server-side error
      if (error.status === 401 || error.status === 403) {
        this.showMessage('Authentication failed. Please login again.', 'danger');
        setTimeout(() => {
          this.router.navigateByUrl('/auth/login');
        }, 2000);
      } else if (error.status === 500) {
        this.showMessage('Server error. Please try again later.', 'danger');
      } else {
        this.showMessage('An unexpected error occurred. Please try again.', 'danger');
      }
    }
  }
}